import { PropsWithRef, RefObject, forwardRef, useState, useMemo } from 'react';
import { SelectProps } from './types';
import { Select as JuicySelect, Preloader } from '@vtbhome/juicyfront';
import { Typography } from '../typography/typography';
import { PreloaderOption, StyledFormGroup } from './select.styles';
import { ISelectProps } from '@vtbhome/juicyfront/components/atoms/Select/Select';
import { SELECT_LIST_EMPTY_TEXT_HIDE_CLASS_NAME } from './constants';

export const Select = forwardRef(
    (
        {
            error,
            label,
            required,
            isShowPreloaderInList,
            infinityScrollProps,
            onChange,
            ...props
        }: PropsWithRef<SelectProps>,
        ref: RefObject<HTMLDivElement>
    ) => {
        const [selectedOptions, setSelectedOptions] = useState<SelectProps['options']>([]);
        const modifiedInfinityScrollProps = useMemo(() => {
            if (isShowPreloaderInList) {
                return {
                    className: isShowPreloaderInList ? SELECT_LIST_EMPTY_TEXT_HIDE_CLASS_NAME : '',
                    endMessage: isShowPreloaderInList && (
                        <PreloaderOption>
                            <Preloader />
                        </PreloaderOption>
                    ),
                    ...infinityScrollProps,
                } as ISelectProps['infinityScrollProps'];
            }
            return infinityScrollProps;
        }, [isShowPreloaderInList, infinityScrollProps]);

        return (
            <StyledFormGroup
                required={required}
                $error={error}
                label={label}
                errorMessage={
                    <>
                        {Boolean(error) && (
                            <Typography size='body2_short' color='error_60'>
                                {error}
                            </Typography>
                        )}
                        {!props.multiselect && Boolean(selectedOptions[0]?.hint) && (
                            <Typography size='body2_short' color='neutral_50'>
                                {selectedOptions[0].hint}
                            </Typography>
                        )}
                    </>
                }>
                <JuicySelect
                    containerRef={ref}
                    invalid={Boolean(error)}
                    infinityScrollProps={modifiedInfinityScrollProps}
                    onChange={(options) => {
                        setSelectedOptions(options);
                        onChange?.(options);
                    }}
                    {...props}
                />
            </StyledFormGroup>
        );
    }
);

Select.displayName = 'Select';
